import { mergeAttributes, Node, nodeInputRule } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { ResizableMediaNodeView } from "./resizable-media-node-view";
import { cn } from "@/utils/cn";

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		resizableMedia: {
			/**
			 * Set media
			 */
			setMedia: (options: ResizableMediaOptions) => ReturnType;
		};
	}
}

type DefaultResizableMediaOptions = {
	alt?: string;
	title?: string;
	width?: string;
	height?: string;
	dataAlign?: string;
};

type ResizableMediaOptions = DefaultResizableMediaOptions &
	(
		| {
				src: string;
		  }
		| {
				fileId: string;
		  }
	);

export interface MediaOptions {
	// inline: boolean, // we have floating support, so block is good enough
	// allowBase64: boolean, // we're not going to allow this
	HTMLAttributes: Record<string, any>;
}

export const IMAGE_INPUT_REGEX =
	/(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;

export const ImageExtension = Node.create<MediaOptions>({
	name: "image",

	addOptions() {
		return {
			HTMLAttributes: {},
		};
	},

	inline: false,

	group: "block",

	draggable: true,

	addAttributes() {
		return {
			fileId: {
				default: null,
			},
			src: {
				default: null,
			},
			alt: {
				default: null,
			},
			title: {
				default: null,
			},
			width: {
				default: "100%",
			},
			height: {
				default: "auto",
			},
			dataAlign: {
				default: "center", // 'left' | 'center' | 'right'
			},
			dataFloat: {
				default: null, // 'left' | 'right'
			},
		};
	},

	selectable: true,

	parseHTML() {
		return [
			{
				tag: 'img[src]:not([src^="data:"])',
				getAttrs: (el) => ({
					src: (el as HTMLImageElement).getAttribute("src"),
					"media-type": "img",
				}),
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			{
				class: cn(
					"flex w-full items-center",
					HTMLAttributes.dataAlign === "center"
						? "justify-center"
						: HTMLAttributes.dataAlign === "right"
							? "justify-end"
							: "justify-start",
				),
			},
			["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)],
		];
	},

	addCommands() {
		return {
			setMedia:
				(options) =>
				({ commands }) => {
					return commands.insertContent([
						{
							type: this.name,
							attrs: options,
						},
						{
							type: "hardBreak",
						},
					]);
				},
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(ResizableMediaNodeView);
	},

	addInputRules() {
		return [
			nodeInputRule({
				find: IMAGE_INPUT_REGEX,
				type: this.type,
				getAttributes: (match) => {
					const [, , alt, src, title] = match;

					return {
						src,
						alt,
						title,
					};
				},
			}),
		];
	},
});

export const ResizableMediExtension = Node.create({
	name: "resizableMedia",

	addOptions() {
		return {
			HTMLAttributes: {},
		};
	},

	inline: false,

	group: "block",

	draggable: true,

	addAttributes() {
		return {
			fileId: {
				default: null,
			},
			src: {
				default: null,
			},
			alt: {
				default: null,
			},
			title: {
				default: null,
			},
			width: {
				default: "100%",
			},
			height: {
				default: "auto",
			},
			dataAlign: {
				default: "center", // 'left' | 'center' | 'right'
			},
			dataFloat: {
				default: null, // 'left' | 'right'
			},
		};
	},

	selectable: true,

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			{
				class: cn(
					"flex w-full items-center",
					HTMLAttributes.dataAlign === "center"
						? "justify-center"
						: HTMLAttributes.dataAlign === "right"
							? "justify-end"
							: "justify-start",
				),
			},
			["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)],
		];
	},

	addCommands() {
		return {
			setMedia:
				(options) =>
				({ commands }) => {
					return commands.insertContent([
						{
							type: this.name,
							attrs: options,
						},
						{
							type: "hardBreak",
						},
					]);
				},
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(ResizableMediaNodeView);
	},

	addInputRules() {
		return [
			nodeInputRule({
				find: IMAGE_INPUT_REGEX,
				type: this.type,
				getAttributes: (match) => {
					const [, , alt, src, title] = match;

					return {
						src,
						alt,
						title,
					};
				},
			}),
		];
	},
});
